import { useEffect, useRef, useState } from 'react';
import { RtmMessage, RtmStatusCode } from 'agora-rtm-sdk';

import { ChannelMessage, ChannelMessageType, PeerMessage, PeerMessageType } from 'types';
import { RTM_CHANNEL_MEMBER_LIMIT } from 'utils';
import { RTMService } from 'services';

export type RTMState = {
  joining: boolean;
  joined: boolean;
  membersCount: number;
};
export type RTMEventHandlers = {};
export type RTMOptions = RTMEventHandlers & {
  onRemovedFromStage(): void;
  onRoleSwitched(promote: boolean): void;
  onInviteeRemovedFromStage(username: string): void;
  onInvitedToStage(): void;
  onHandRaiseApproved?(): void;
  onHandRaiseRejected?(): void;
  onHandRaiseEnabled?(): void;
  onHandRaiseDisabled?(): void;
  onHandRaised?(): void;
  onHandDropped?(): void;
  onAudioMuted(): void;
  onEnded(): void;
  onMemberLimitReached(): void;
  onJoiningDeclined?(username: string, name: string, isInvitee: boolean): void;
  onJoinedFromAnotherSession?(): void;
};
export type RTMResult = {
  state: RTMState;
  membersUIDs: string[];
  join(uid: string, token?: string): Promise<void>;
  leave(): Promise<void>;
  sendPeerMessage(peerId: string, message: PeerMessage): Promise<void>;
  sendChannelMessage(message: ChannelMessage): Promise<void>;
};

export const useRTM = (
  channelName: string,
  {
    onRemovedFromStage,
    onInviteeRemovedFromStage,
    onRoleSwitched,
    onInvitedToStage,
    onHandRaiseApproved,
    onHandRaiseRejected,
    onHandRaiseEnabled,
    onHandRaiseDisabled,
    onHandRaised,
    onHandDropped,
    onAudioMuted,
    onEnded,
    onMemberLimitReached,
    onJoiningDeclined,
    onJoinedFromAnotherSession
  }: RTMOptions
): RTMResult => {
  const [state, setState] = useState<RTMState>({
    joining: false,
    joined: false,
    membersCount: 1
  });
  const membersUIDsRef = useRef<string[]>([]);

  useEffect(() => {
    const onConnectionStateChanged = (
      newState: RtmStatusCode.ConnectionState,
      reason: RtmStatusCode.ConnectionChangeReason
    ) => {
      if (newState === 'ABORTED' && reason === 'REMOTE_LOGIN') {
        onJoinedFromAnotherSession?.();
      }
    };

    RTMService.client.on('ConnectionStateChanged', onConnectionStateChanged);

    return () => {
      RTMService.client.off('ConnectionStateChanged', onConnectionStateChanged);
    };
  }, [onJoinedFromAnotherSession]);

  // listen members count every 10 seconds effect
  useEffect(() => {
    const { [channelName]: channel } = RTMService.channels;

    const intervalId = setInterval(async () => {
      const members = await channel?.getMembers();

      if (members) {
        membersUIDsRef.current = [...members];

        setState(prev => ({
          ...prev,
          membersCount: members.length
        }));
      }
    }, 10_000);

    return () => {
      clearInterval(intervalId);
    };
  }, [channelName, state.joined]);

  // handle on attributes updated effect
  useEffect(() => {
    const { [channelName]: channel } = RTMService.channels;
    const handleOnAttributesUpdated = rawAttributes => {};

    channel?.on('AttributesUpdated', handleOnAttributesUpdated);

    return () => {
      channel?.off('AttributesUpdated', handleOnAttributesUpdated);
    };
  }, [channelName, state.joined]);

  // handle on member count updated effect
  useEffect(() => {
    const { [channelName]: channel } = RTMService.channels;
    const handleOnMemberCountUpdated = (membersCount: number) => setState(prev => ({ ...prev, membersCount }));

    channel?.on('MemberCountUpdated', handleOnMemberCountUpdated);

    return () => {
      channel?.off('MemberCountUpdated', handleOnMemberCountUpdated);
    };
  }, [channelName, state.joined]);

  // handle on member joined effect
  useEffect(() => {
    const { [channelName]: channel } = RTMService.channels;
    const handleOnMemberJoined = (memberId: string) => {
      membersUIDsRef.current = [...membersUIDsRef.current, memberId];
    };

    channel?.on('MemberJoined', handleOnMemberJoined);

    return () => {
      channel?.off('MemberJoined', handleOnMemberJoined);
    };
  }, [channelName, state.joined]);

  // handle on member left effect
  useEffect(() => {
    const { [channelName]: channel } = RTMService.channels;
    const handleOnMemberLeft = (memberId: string) => {
      membersUIDsRef.current = membersUIDsRef.current.filter(_uid => _uid !== memberId);
    };

    channel?.on('MemberLeft', handleOnMemberLeft);

    return () => {
      channel?.off('MemberLeft', handleOnMemberLeft);
    };
  }, [channelName, state.joined]);

  // handle on receive channel message effect
  useEffect(() => {
    const { [channelName]: channel } = RTMService.channels;
    const handleOnReceiveChannelMessage = (rawMessage: RtmMessage) => {
      if (rawMessage.messageType !== 'TEXT') return;

      const parsedMessage: ChannelMessage = JSON.parse(rawMessage.text);

      switch (parsedMessage.type) {
        case ChannelMessageType.END_EVENT: {
          onEnded();
          break;
        }

        case ChannelMessageType.ENABLE_HAND_RAISE: {
          onHandRaiseEnabled?.();
          break;
        }

        case ChannelMessageType.DISABLE_HAND_RAISE: {
          onHandRaiseDisabled?.();
          break;
        }

        case ChannelMessageType.RAISE_HAND: {
          onHandRaised?.();
          break;
        }

        case ChannelMessageType.DROP_HAND: {
          onHandDropped?.();
          break;
        }
      }
    };

    channel?.on('ChannelMessage', handleOnReceiveChannelMessage);

    return () => {
      channel?.off('ChannelMessage', handleOnReceiveChannelMessage);
    };
  }, [channelName, state.joined, onEnded, onHandDropped, onHandRaiseDisabled, onHandRaiseEnabled, onHandRaised]);

  // handle on received message from peer effect
  useEffect(() => {
    const { client } = RTMService;
    const handleOnReceiveMessageFromPeer = (rawMessage: RtmMessage) => {
      if (rawMessage.messageType !== 'TEXT') return;

      const parsedMessage: PeerMessage = JSON.parse(rawMessage.text);

      switch (parsedMessage.type) {
        case PeerMessageType.PROMOTE_TO_MODERATOR:
          onRoleSwitched(true);
          break;

        case PeerMessageType.DEMOTE_FROM_MODERATOR:
          onRoleSwitched(false);
          break;

        case PeerMessageType.REMOVE_FROM_STAGE:
          onRemovedFromStage();
          break;

        case PeerMessageType.REMOVE_INVITEE_FROM_STAGE:
          onInviteeRemovedFromStage(parsedMessage.payload.username);
          break;

        case PeerMessageType.INVITE_TO_STAGE:
          onInvitedToStage();
          break;

        case PeerMessageType.MUTE_AUDIO:
          onAudioMuted();
          break;

        case PeerMessageType.APPROVE_HAND_RAISE:
          onHandRaiseApproved?.();
          break;

        case PeerMessageType.REJECT_HAND_RAISE:
          onHandRaiseRejected?.();
          break;

        case PeerMessageType.DECLINE_JOINING:
          onJoiningDeclined?.(
            parsedMessage.payload.username,
            parsedMessage.payload.name,
            parsedMessage.payload.isInvitee
          );
          break;
      }
    };

    client?.on('MessageFromPeer', handleOnReceiveMessageFromPeer);

    return () => {
      client?.off('MessageFromPeer', handleOnReceiveMessageFromPeer);
    };
  }, [
    channelName,
    state.joined,
    onAudioMuted,
    onHandRaiseApproved,
    onHandRaiseRejected,
    onInvitedToStage,
    onInviteeRemovedFromStage,
    onJoiningDeclined,
    onRemovedFromStage,
    onRoleSwitched
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      (!!event.returnValue && event.returnValue !== '') || RTMService.leave(channelName);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [channelName]);

  const join = async (uid: string, token?: string) => {
    setState(prev => ({ ...prev, joined: false, joining: true }));

    await RTMService.login({ uid, token });
    await RTMService.join(channelName);
    await getInitialMembersCount();

    setState(prev => ({ ...prev, joined: true, joining: false }));
  };

  const leave = async () => {
    await RTMService.leave(channelName);
    await RTMService.logout();

    setState(prev => ({ ...prev, joined: false }));
  };

  const getInitialMembersCount = async () => {
    const { [channelName]: channel } = RTMService.channels;

    if (channel) {
      const members = await channel?.getMembers();

      if (members.length > RTM_CHANNEL_MEMBER_LIMIT) onMemberLimitReached();

      membersUIDsRef.current = [...members];

      setState(prev => ({
        ...prev,
        membersCount: members?.length
      }));
    }
  };

  const sendPeerMessage = (peerId: string, message: PeerMessage) => RTMService.sendPeerMessage(peerId, message);

  const sendChannelMessage = (message: ChannelMessage) => RTMService.sendChannelMessage(channelName, message);

  return {
    state,
    membersUIDs: membersUIDsRef.current,
    join,
    leave,
    sendPeerMessage,
    sendChannelMessage
  };
};
